import { DEFAULT_CITY, type CurrentCity, type CityListItem } from '@src/api/city';
import { TMapLoader } from './mapCore';

// ---------------------------- IP 定位与城市匹配 ----------------------------

/**
 * adcode 前缀与 areaId 是否从字符串头部互相兼容（模糊前缀匹配）。
 * 二者长度可以不同，例如 adcode 前 3 位 `340` 可匹配 areaId `3401`。
 */
const isAdcodeCityIdPrefixMatch = (adcodePrefix: string, areaId: string | number): boolean => {
    const prefix = String(adcodePrefix);
    const idStr = String(areaId);
    if (!prefix || !idStr) return false;
    return prefix.startsWith(idStr) || idStr.startsWith(prefix);
};

interface CityMatchResult {
    cityInfo: CityListItem | null;
    areaId: string | number;
}

/**
 * 取 adcode 头部若干位，在城市列表中做模糊前缀匹配。
 * 多个命中时：优先完全相等，否则按 areaId 纯数字大小取最小的一项。
 */
const findCityByAdcodeHead = (adcodeStr: string, cities: CityListItem[], headLen: number): CityMatchResult | null => {
    const adcodePrefix = adcodeStr.slice(0, headLen);
    if (!adcodePrefix) return null;

    const matched = cities.filter(city => isAdcodeCityIdPrefixMatch(adcodePrefix, city.areaId));
    if (!matched.length) return null;

    const cityInfo =
        matched.find(city => String(city.areaId) === adcodePrefix) ?? matched.sort((a, b) => Number(a.areaId) - Number(b.areaId))[0];

    return { cityInfo, areaId: cityInfo.areaId };
};

/**
 * 根据腾讯 IP 定位返回的 adcode，在城市列表中解析匹配项。
 *
 * geo/list 标准化后 areaId 为地级行政区划（如合肥 3401）；接口 adcode 至少 4 位（如 340000、340104）。
 * 匹配时截取 adcode **头部**若干位，与 areaId 做字符串前缀模糊匹配（长度不必相同）。
 *
 * 匹配顺序：
 * 1. adcode 前 4 位模糊匹配，如 340104 → 3401
 * 2. 未命中则 adcode 前 3 位再试，如 340000 → 3401（340 ⊂ 3401）
 * 3. 仍未命中则回退 DEFAULT_CITY.areaId（3401 合肥）
 */
const resolveCityInfoByAdcode = (adcode: string | number | undefined, cities: CityListItem[]): CityMatchResult => {
    const adcodeStr = String(adcode ?? '').replace(/\D/g, '');
    const fallback = (): CityMatchResult => ({
        cityInfo: cities.find(city => String(city.areaId) === String(DEFAULT_CITY.areaId)) ?? null,
        areaId: DEFAULT_CITY.areaId,
    });

    if (!adcodeStr) return fallback();

    return findCityByAdcodeHead(adcodeStr, cities, 4) || findCityByAdcodeHead(adcodeStr, cities, 3) || fallback();
};

/** 腾讯 IP 定位；空 IP 失败时用备用 IP 重试，仍失败返回 null */
const locateByIp = async (TMap: TMapNamespace) => {
    const ipLocation = new TMap.service.IPLocation();

    try {
        return await ipLocation.locate({ ip: '', servicesk: '' });
    } catch {
        return await ipLocation.locate({ ip: '1.2.2.255', servicesk: '' }).catch(() => null);
    }
};

/**
 * IP 定位当前城市，并与全量城市列表匹配出完整城市信息。
 * 全流程不抛出：SDK 加载失败、定位失败或 adcode 未匹配时均回退 DEFAULT_CITY。
 */
export const locateCityByIp = async (cityList: CityListItem[]): Promise<CurrentCity> => {
    const locationRes = await TMapLoader(import.meta.env.VITE_APP_TENCENT_MAP_KEY)
        .then(locateByIp)
        .catch(() => null);
    console.log('[ip-location] 腾讯 IP 定位结果', locationRes);

    const { ad_info, location } = locationRes?.result || {};
    const { cityInfo, areaId } = resolveCityInfoByAdcode(ad_info?.adcode, cityList);

    return {
        id: cityInfo?.mapId || DEFAULT_CITY.id,
        name: cityInfo?.name || ad_info?.city || DEFAULT_CITY.name,
        mapId: cityInfo?.mapId || DEFAULT_CITY.mapId,
        areaId,
        latlon: location?.lng != null && location?.lat != null ? [location.lng, location.lat] : [...DEFAULT_CITY.latlon],
        automaticPositioning: true,
    };
};
